import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { ChevronLeft, Sparkles } from "lucide-react";
import { useAuth } from "@/hooks/use-auth";
import { supabase } from "@/lib/supabase";
import { AppShell } from "@/components/app-shell";
import { ImageBox } from "@/components/ui-extras";
import { EmptyState } from "@/components/empty-state";
import { BG_PREFERENCE_SELECT, bgFields, resolvePreferredPhoto } from "@/lib/photo-preference";

export const Route = createFileRoute("/rediscover")({
  head: () => ({
    meta: [
      { title: "Rediscover — Cailève" },
      { name: "description", content: "Pieces you haven't worn lately." },
    ],
  }),
  component: () => (
    <AppShell>
      <RediscoverPage />
    </AppShell>
  ),
});

type RediscoverItem = {
  kind: "jewelry" | "clothing" | "accessory";
  id: string;
  name: string;
  photo_url: string | null;
  category: string | null;
  photo_url_no_bg: string | null;
  prefer_clean_bg: boolean | null;
  clean_bg_color: string | null;
};

function RediscoverPage() {
  const { user } = useAuth();
  const uid = user?.id;

  const { data: items } = useQuery({
    queryKey: ["rediscover_page", uid],
    enabled: !!uid,
    staleTime: 60 * 60 * 1000,
    queryFn: async () => {
      const sixtyDaysAgo = new Date(Date.now() - 60 * 24 * 60 * 60 * 1000)
        .toISOString()
        .slice(0, 10);
      const [j, c, a, worn] = await Promise.all([
        supabase
          .from("jewelry_items")
          .select(`id, name, photo_url, category, ${BG_PREFERENCE_SELECT}`)
          .eq("user_id", uid!)
          .eq("is_wishlist", false)
          .eq("is_archived", false)
          .eq("is_accessory", false)
          .order("last_surfaced_at", { ascending: true, nullsFirst: true }),
        supabase
          .from("clothing_items")
          .select(`id, name, photo_url, clothing_type, ${BG_PREFERENCE_SELECT}`)
          .eq("user_id", uid!)
          .eq("is_wishlist", false)
          .eq("is_archived", false)
          .order("last_surfaced_at", { ascending: true, nullsFirst: true }),
        supabase
          .from("jewelry_items")
          .select(`id, name, photo_url, category, ${BG_PREFERENCE_SELECT}`)
          .eq("user_id", uid!)
          .eq("is_wishlist", false)
          .eq("is_archived", false)
          .eq("is_accessory", true)
          .order("last_surfaced_at", { ascending: true, nullsFirst: true }),
        supabase
          .from("wear_logs")
          .select("item_id")
          .eq("user_id", uid!)
          .gte("worn_at", sixtyDaysAgo),
      ]);
      const wornIds = new Set((worn.data ?? []).map((w) => w.item_id));
      const merged: RediscoverItem[] = [
        ...((j.data ?? []) as Array<{ id: string; name: string; photo_url: string | null; category: string | null } & Record<string, unknown>>).map(
          (x) => ({ kind: "jewelry" as const, id: x.id, name: x.name, photo_url: x.photo_url, category: x.category, ...bgFields(x) }),
        ),
        ...((c.data ?? []) as Array<{ id: string; name: string; photo_url: string | null; clothing_type: string | null } & Record<string, unknown>>).map(
          (x) => ({
            kind: "clothing" as const,
            id: x.id,
            name: x.name,
            photo_url: x.photo_url,
            category: x.clothing_type,
            ...bgFields(x),
          }),
        ),
        ...((a.data ?? []) as Array<{ id: string; name: string; photo_url: string | null; category: string | null } & Record<string, unknown>>).map(
          (x) => ({ kind: "accessory" as const, id: x.id, name: x.name, photo_url: x.photo_url, category: x.category, ...bgFields(x) }),
        ),
      ].filter((item) => !wornIds.has(item.id));
      return merged.slice(0, 10);
    },
  });

  if (!uid) return null;

  const linkTo = (kind: "jewelry" | "clothing" | "accessory") =>
    kind === "jewelry" ? "/jewelry/$id" : kind === "clothing" ? "/clothing/$id" : "/accessories/$id";

  return (
    <div className="max-w-7xl mx-auto px-8 py-8">
      <Link
        to="/"
        className="inline-flex items-center gap-1 text-sm mb-6"
        style={{ color: "var(--caileve-muted)", textDecoration: "none" }}
      >
        <ChevronLeft className="h-4 w-4" strokeWidth={1.5} /> Home
      </Link>

      <div className="mb-8 border-b border-[var(--caileve-copper)]/25 pb-4">
        <h1
          className="font-serif-display text-3xl text-[var(--caileve-ink)]"
          style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
        >
          Rediscover
        </h1>
        <p
          className="italic text-base mt-1"
          style={{
            fontFamily: "'Cormorant Garamond', Georgia, serif",
            color: "var(--caileve-muted)",
          }}
        >
          Pieces you haven't worn lately.
        </p>
      </div>

      {items && items.length > 0 ? (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-5">
          {items.map((item) => {
            const resolved = resolvePreferredPhoto(item);
            return (
            <div key={`${item.kind}-${item.id}`} className="block">
              <Link
                to={linkTo(item.kind)}
                params={{ id: item.id }}
                className="group block"
              >
                <ImageBox src={resolved.src} alt={item.name} style={resolved.backdropStyle} />
                <p className="font-serif-display text-lg mt-3 leading-tight text-[var(--caileve-ink)]">
                  {item.name}
                </p>
                {item.category && (
                  <p
                    className="uppercase mt-1"
                    style={{ fontSize: 10, letterSpacing: "0.25em", color: "var(--caileve-muted)" }}
                  >
                    {item.category}
                  </p>
                )}
              </Link>
              <Link
                to="/suggest"
                search={{
                  jewelryId: item.kind === "jewelry" ? item.id : undefined,
                  clothingId: item.kind === "clothing" ? item.id : undefined,
                  accessoryId: item.kind === "accessory" ? item.id : undefined,
                }}
                className="inline-flex items-center gap-1 mt-2 mb-4 px-3 py-1 rounded-full text-xs"
                style={{
                  border: "1px solid var(--caileve-gold)",
                  color: "var(--caileve-gold-text)",
                  textDecoration: "none",
                  fontFamily: "'Cormorant Garamond', Georgia, serif",
                  fontStyle: "italic",
                }}
              >
                <Sparkles className="h-3 w-3" strokeWidth={1.5} />
                Style this
              </Link>
            </div>
            );
          })}
        </div>
      ) : (
        <EmptyState
          icon={Sparkles}
          message="Nothing to rediscover yet."
          subMessage="Add pieces to your collection — Cailève will surface the ones waiting for another moment."
        />
      )}
    </div>
  );
}